import { useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { addIdleTask, clearIdleTask } from '@trello/idle-task-scheduler';

const JS_ASSET_PATTERN = new RegExp(
  '(prgb/)?dist/(br/)?([@a-zA-Z._-]+)\\.[a-z0-9]{20}\\.js$',
);

const isPerformanceResourceTiming = (
  entry: PerformanceEntry | PerformanceResourceTiming,
): entry is PerformanceResourceTiming => {
  return 'initiatorType' in entry;
};

const isJavascriptAsset = ({
  initiatorType,
  name,
}: PerformanceResourceTiming) => {
  return initiatorType === 'script' && name.endsWith('.js');
};

interface EventMetric {
  name: string;
  assetName?: string;
  duration: number;
}

const convertTimingToEventMetric = ({
  name,
  duration,
}: PerformanceResourceTiming): EventMetric => {
  let assetName;
  if (JS_ASSET_PATTERN.test(name)) {
    const [, , , asset] = name.match(JS_ASSET_PATTERN) || [];
    if (asset) {
      assetName = asset;
    }
  }

  return {
    name,
    assetName,
    duration,
  };
};

export const sendPerformanceMetrics = () => {
  const sendingEnabled = featureFlagClient.get(
    'fep.send-startup-performance-metrics',
    false,
  );

  if (!sendingEnabled) {
    return;
  }

  const resourceMetrics = window.performance
    .getEntriesByType('resource')
    .filter(isPerformanceResourceTiming)
    .filter(isJavascriptAsset)
    .map(convertTimingToEventMetric);

  Analytics.sendOperationalEvent({
    action: 'measured',
    actionSubject: 'performance',
    source: 'appStartup',
    attributes: {
      resources: resourceMetrics,
    },
  });
};

export const usePerformanceTracking = () => {
  useEffect(() => {
    // Ideally we would call sendPerformanceMetrics after a page has completed
    // rendering. Since we currently have no deterministic way of doing this
    // right now, we're using a delay to capture what we can
    const IDLE_TASK_DELAY = 15000;

    const taskId = addIdleTask(() => {
      sendPerformanceMetrics();
    }, IDLE_TASK_DELAY);

    return () => clearIdleTask(taskId);
  }, []);
};
